import type { SelectTriggerProps, SelectValueProps } from '@base-ui/react';
import type { ReactNode } from 'react';

import { Select as BaseSelect } from '@base-ui/react';
import { useTranslation } from 'react-i18next';

import { clsxs } from '~/styles/utils';

import { Icon, type IconName } from '../../Icon/Icon';
import { adornmentStyle, controlStateTarget, controlThemeStyle, prefixIconStyle } from '../form.css';
import { useAccessibleAffixes } from '../form.hooks';
import { chevronIconStyle, emphasisSelectButtonStyles, multipleValuesLengthStyle, triggerStyle, valueStyle } from './SelectButton.css';

type Props = Omit<SelectTriggerProps, 'children'> &
  Pick<SelectValueProps, 'children' | 'placeholder'> & {
    adornment?: ReactNode;
    emphasis?: 'secondary' | 'tertiary';
    icon?: IconName;
    prefix?: string;
    suffix?: string;
  };

export const SelectButton = ({ adornment, children, emphasis = 'secondary', icon, placeholder, prefix, suffix, ...delegated }: Props) => {
  const { ariaDescribedBy, prefixProps, suffixProps } = useAccessibleAffixes({ prefix, suffix });

  const { t } = useTranslation(['common']);

  return (
    <BaseSelect.Trigger
      {...delegated}
      className={clsxs(controlStateTarget, triggerStyle, emphasisSelectButtonStyles[emphasis], delegated.className)}
      render={(triggerProps, state) => (
        <div className={controlThemeStyle}>
          <button
            {...triggerProps}
            aria-describedby={[triggerProps['aria-describedby'], ariaDescribedBy].filter(Boolean).join(' ') || undefined}
          >
            {icon && <Icon className={prefixIconStyle} name={icon} />}

            {adornment && <span className={adornmentStyle}>{adornment}</span>}

            {prefix && <span {...prefixProps}>{prefix}</span>}

            <BaseSelect.Value className={valueStyle} placeholder={placeholder}>
              {children}
            </BaseSelect.Value>

            {Array.isArray(state.value) && state.value.length > 0 && (
              <span
                aria-label={t('common:fields.select.multipleValuesAriaLabel', { count: state.value.length })}
                className={multipleValuesLengthStyle}
              >
                {state.value.length}
              </span>
            )}

            <BaseSelect.Icon className={chevronIconStyle}>
              <Icon name="ChevronDown" />
            </BaseSelect.Icon>

            {suffix && <span {...suffixProps}>{suffix}</span>}
          </button>
        </div>
      )}
    />
  );
};
